import { useEffect, useMemo, useRef, useState } from "react";

import { Skeleton } from "@/components/ui/Skeleton";
import { Text } from "@/components/ui/Typography";
import { cn } from "@/lib/cn";
import { useImagePreload } from "@/hooks/use-image-preload";
import { useInViewport } from "@/hooks/use-in-viewport";
import {
  type BunnyAutoOptimize,
  type BunnyImageFormat,
  bunnyImageUrl,
  responsiveWidths,
} from "@/lib/bunny-image";

export type ProgressiveImageProps = {
  /** Absolute image URL (served through the Bunny Optimizer pull zone). */
  src: string;
  alt: string;
  /** Intrinsic layout width in px — drives the aspect ratio and srcset scale. */
  width: number;
  /** Intrinsic layout height in px. Falls back to a square when omitted. */
  height?: number;
  /** Classes for the container box (sizing, rounding, opacity…). */
  className?: string;
  /**
   * Responsive `sizes` hint. Defaults to `${width}px`; pass a media-query form
   * (e.g. `(max-width: 768px) 100vw, 320px`) for fluid layouts.
   */
  sizes?: string;
  /** Full-image compression (0–100). Ignored if the Bunny class locks quality. */
  quality?: number;
  /**
   * Output format forced via Bunny's `format` param. Defaults to `webp` so
   * every image is served as WebP regardless of the zone's auto-negotiation.
   * Pass `null` to let Bunny negotiate (WebP/AVIF) from the `Accept` header.
   */
  format?: BunnyImageFormat | null;
  /** Optional Bunny multi-pass optimization (`high` also sharpens). */
  autoOptimize?: BunnyAutoOptimize | null;
  /**
   * Above-the-fold? Loads eagerly with `fetchpriority=high`, skips the
   * IntersectionObserver, and emits a `<link rel=preload>` for LCP.
   */
  priority?: boolean;
  /** Cache-busting token (image CRC/version) forwarded as Bunny's `v` param. */
  crc?: string;
  /** Image URL shown if the full image fails to load. */
  fallback?: string;
  objectFit?: "cover" | "contain";
  /** Bunny Optimizer Class for the full image. `null` uses plain params only. */
  fullClass?: string | null;
  /** Bunny Optimizer Class for the LQIP. `null` requests a tiny 32px/q10 crop. */
  placeholderClass?: string | null;
  /** Upper bound for srcset candidates (avoids over-fetching on huge screens). */
  maxWidth?: number;
  /** Pre-load margin around the viewport for lazy images. */
  rootMargin?: string;
  onLoad?: () => void;
};

const DEFAULT_QUALITY = 78;

export function ProgressiveImage({
  src,
  alt,
  width,
  height,
  className,
  sizes,
  quality = DEFAULT_QUALITY,
  format = "webp",
  autoOptimize = null,
  priority = false,
  crc,
  fallback,
  objectFit = "cover",
  fullClass = "full",
  placeholderClass = "placeholder",
  maxWidth,
  rootMargin,
  onLoad,
}: ProgressiveImageProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const fullImgRef = useRef<HTMLImageElement>(null);
  const placeholderImgRef = useRef<HTMLImageElement>(null);
  const inView = useInViewport(containerRef, { rootMargin, disabled: priority });
  const shouldLoad = priority || inView;
  const hasSource = src.trim().length > 0;

  const [placeholderLoaded, setPlaceholderLoaded] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [settled, setSettled] = useState(false);
  const [errored, setErrored] = useState(false);

  const { fullSrc, srcSet } = useMemo(() => {
    if (!shouldLoad || !hasSource) return { fullSrc: undefined, srcSet: undefined };

    const build = (candidateWidth: number) =>
      bunnyImageUrl(src, {
        imageClass: fullClass ?? undefined,
        width: candidateWidth,
        height: height ? Math.round((candidateWidth * height) / width) : undefined,
        quality,
        format: format ?? undefined,
        autoOptimize: autoOptimize ?? undefined,
        version: crc,
      });

    const candidates = responsiveWidths(width, maxWidth ? { max: maxWidth } : undefined);
    return {
      fullSrc: build(width),
      srcSet: candidates.map((candidate) => `${build(candidate)} ${candidate}w`).join(", "),
    };
  }, [shouldLoad, hasSource, src, fullClass, width, height, quality, format, autoOptimize, crc, maxWidth]);

  const placeholderSrc = useMemo(() => {
    if (!shouldLoad || !hasSource) return undefined;
    return bunnyImageUrl(src, {
      imageClass: placeholderClass ?? undefined,
      width: placeholderClass ? undefined : 32,
      quality: placeholderClass ? undefined : 10,
      format: format ?? undefined,
      version: crc,
    });
  }, [shouldLoad, hasSource, src, placeholderClass, format, crc]);

  const sizesValue = sizes ?? `${width}px`;

  const [trackedFullSrc, setTrackedFullSrc] = useState(fullSrc);
  if (trackedFullSrc !== fullSrc) {
    setTrackedFullSrc(fullSrc);
    setLoaded(false);
    setSettled(false);
    setErrored(false);
  }

  const [trackedPlaceholderSrc, setTrackedPlaceholderSrc] = useState(placeholderSrc);
  if (trackedPlaceholderSrc !== placeholderSrc) {
    setTrackedPlaceholderSrc(placeholderSrc);
    setPlaceholderLoaded(false);
  }

  useEffect(() => {
    const full = fullImgRef.current;
    if (full && full.complete && full.naturalWidth > 0) setLoaded(true);

    const placeholder = placeholderImgRef.current;
    if (placeholder && placeholder.complete && placeholder.naturalWidth > 0) {
      setPlaceholderLoaded(true);
    }
  }, [fullSrc, placeholderSrc]);

  useEffect(() => {
    if (!loaded) return;
    const raf = requestAnimationFrame(() => setSettled(true));
    return () => cancelAnimationFrame(raf);
  }, [loaded]);

  useImagePreload({
    enabled: priority && shouldLoad && hasSource && !errored,
    srcSet,
    sizes: sizesValue,
    href: fullSrc,
  });

  const showError = errored || !hasSource;
  const objectFitClass = objectFit === "contain" ? "object-contain" : "object-cover";
  const fadeClass = "transition-opacity duration-emphasis ease-enter motion-reduce:transition-none";

  return (
    <div
      ref={containerRef}
      className={cn("relative w-full overflow-hidden", objectFit === "contain" && "bg-media-mat", className)}
      style={{ aspectRatio: `${width} / ${height ?? width}` }}
    >
      {!settled && !showError && (
        <Skeleton shape="flat" className="absolute inset-0 h-full w-full" />
      )}

      {placeholderSrc && !settled && !showError && (
        <img
          ref={placeholderImgRef}
          src={placeholderSrc}
          alt=""
          aria-hidden
          decoding="async"
          onLoad={() => setPlaceholderLoaded(true)}
          className={cn(
            "absolute inset-0 h-full w-full scale-105 blur-xl",
            objectFitClass,
            fadeClass,
            placeholderLoaded ? "opacity-100" : "opacity-0",
          )}
        />
      )}

      {fullSrc && !showError && (
        <img
          ref={fullImgRef}
          src={fullSrc}
          srcSet={srcSet}
          sizes={sizesValue}
          alt={alt}
          decoding="async"
          loading={priority ? "eager" : "lazy"}
          {...(priority ? { fetchpriority: "high" } : {})}
          onLoad={() => {
            setLoaded(true);
            onLoad?.();
          }}
          onError={() => setErrored(true)}
          className={cn(
            "absolute inset-0 h-full w-full",
            objectFitClass,
            fadeClass,
            loaded ? "opacity-100" : "opacity-0",
          )}
        />
      )}

      {showError && fallback && (
        <img
          src={fallback}
          alt={alt}
          className={cn("absolute inset-0 h-full w-full", objectFitClass)}
        />
      )}

      {showError && !fallback && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-scale-100">
          <Text as="span" size="sm" tone="subtle">Imagem indisponível</Text>
        </div>
      )}
    </div>
  );
}
